import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Globe, CheckCircle, Clock, AlertCircle, 
  Copy, ExternalLink, RefreshCw, Building2,
  MapPin, Phone, Mail, Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CitationManager = () => {
  const [citationReport, setCitationReport] = useState(null);
  const [citationStatus, setCitationStatus] = useState(null);
  const [napData, setNapData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportRes, statusRes, napRes] = await Promise.all([
        axios.get(`${API}/admin/citations/report`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/admin/citations/status`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/admin/citations/nap-data`, { withCredentials: true }).catch(() => null)
      ]);

      if (reportRes?.data) setCitationReport(reportRes.data);
      if (statusRes?.data) setCitationStatus(statusRes.data);
      if (napRes?.data) setNapData(napRes.data);
    } catch (error) {
      console.error('Error fetching citation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const markAsSubmitted = async (directoryName) => {
    try {
      await axios.post(`${API}/admin/citations/track`, {
        directory_name: directoryName,
        status: 'submitted',
        notes: `Submitted on ${new Date().toLocaleDateString()}`
      }, { withCredentials: true });
      
      toast.success(`${directoryName} marked as submitted`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getPriorityColor = (priority) => {
    if (priority === 1) return 'bg-red-100 text-red-800 border-red-200';
    if (priority === 2) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const getPriorityLabel = (priority) => {
    if (priority === 1) return 'Critical';
    if (priority === 2) return 'Important';
    return 'Recommended';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading citation data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Globe className="w-6 sm:w-7 h-6 sm:h-7 text-blue-600" />
            Directory & Citation Manager
          </h2>
          <p className="text-gray-500 text-sm sm:text-base">Manage business listings for local SEO</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Progress Card */}
      {citationStatus && (
        <Card className="p-4 sm:p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold">{citationStatus.total_directories || 12}</div>
              <p className="text-xs sm:text-sm opacity-80">Total Directories</p>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold">{citationStatus.submitted || 0}</div>
              <p className="text-xs sm:text-sm opacity-80">Submitted</p>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold">{citationStatus.pending || 12}</div>
              <p className="text-xs sm:text-sm opacity-80">Pending</p>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold">{Math.round(citationStatus.completion_rate || 0)}%</div>
              <p className="text-xs sm:text-sm opacity-80">Complete</p>
            </div>
          </div>
        </Card>
      )}

      {/* NAP Consistency Card */}
      {napData && (
        <Card className="p-6">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-orange-500" />
            NAP Consistency Data (Copy Exactly)
          </h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{napData.standard?.name}</span>
              </div>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => copyToClipboard(napData.standard?.name, 'Business name')}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>{napData.standard?.address}</span>
              </div>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => copyToClipboard(napData.standard?.address, 'Address')}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>{napData.standard?.phone}</span>
              </div>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => copyToClipboard(napData.standard?.phone, 'Phone')}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="pt-2 border-t">
              <Button 
                size="sm"
                onClick={() => copyToClipboard(napData.single_line, 'Full NAP')}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Full NAP Line
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Directory List */}
      <Card className="p-6">
        <h3 className="text-lg font-bold mb-4">Directory Submission Checklist</h3>
        <div className="space-y-3">
          {citationReport?.directories?.map((directory, index) => {
            const isSubmitted = citationStatus?.submissions?.some(
              s => s.directory === directory.name && s.status === 'submitted'
            );
            
            return (
              <div 
                key={index}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  isSubmitted ? 'bg-green-50 border-green-200' : 'bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isSubmitted ? 'bg-green-500 text-white' : 'bg-gray-200'
                  }`}>
                    {isSubmitted ? <CheckCircle className="w-5 h-5" /> : <Clock className="w-5 h-5 text-gray-500" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{directory.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityColor(directory.priority)}`}>
                        {getPriorityLabel(directory.priority)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{directory.notes}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a 
                    href={directory.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button size="sm" variant="outline">
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Visit
                    </Button>
                  </a>
                  {!isSubmitted && (
                    <Button 
                      size="sm"
                      onClick={() => markAsSubmitted(directory.name)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Done
                    </Button>
                  )}
                </div>
              </div>
            );
          }) || (
            <div className="text-center py-8 text-gray-500">
              <Globe className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>Click &quot;Refresh&quot; to load directory list</p>
            </div>
          )}
        </div>
      </Card>

      {/* Tips Card */}
      <Card className="p-6 bg-amber-50 border-amber-200">
        <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-600" />
          Citation Best Practices
        </h3>
        <ul className="space-y-2 text-sm text-amber-900">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <span><strong>NAP Consistency:</strong> Use EXACT same name, address, and phone everywhere</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <span><strong>Priority 1 First:</strong> Google Business Profile is the most important citation</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <span><strong>Add Photos:</strong> Listings with photos get 35% more clicks</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <span><strong>Verify Ownership:</strong> Always complete verification for full benefits</span>
          </li>
        </ul>
      </Card>
    </div>
  );
};

export default CitationManager;
